import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { navigate } from '../router/router';
import { useScreen } from '../router/screens';
import { useSettings } from '../platform/settings';
import { PlayerSurface } from '../screens/watch/view';
import { miniStageNotice } from '../screens/watch/model';
import { useWatchScreen, type WatchTarget } from '../screens/watch/useWatchScreen';
import { PlaybackProvider } from './context';
import { createPlayerDock } from './dock';
import { maximizeHref, offRouteTarget, sameWatchTarget } from './model';
import { QueueEndedDock } from './QueueEnded';
import { queueEntryHref, queueLastEntry, queueWatchTarget } from '../queue/model';
import { useHandedOver } from '../tabs/store';
import {
  clearQueue,
  playQueueEntry,
  readQueue,
  useQueue,
  useQueuePlayOnArrival,
} from '../queue/store';

/**
 * The playback session, hoisted above the router for the minimized player.
 *
 * The requirement is one sentence — "transitioning between /watch and the minimized player doesn't
 * interrupt playback" — and it moves the whole watch pipeline, not just a `<video>`. Everything that
 * runs *around* a playing video lives in `useWatchScreen`: the resume-position writer, the watched
 * -state mirror, SponsorBlock, the media session, the 403 recovery. Handing a bare element and a
 * `PlayerPort` to a corner widget would keep the picture moving and silently stop all five, which is
 * the kind of regression nothing on screen reports. So the *hook* is what survives the navigation,
 * and the page below merely borrows its binding (`context.ts`).
 *
 * Three things follow, and each has its own file because each is a separate hazard:
 *
 * - the surface's DOM must move without ever being detached — `dock.ts`
 * - Maximize must return to the *exact* route it left, or the player reloads — `model.ts`
 * - the keymap and auto-advance must stand down while minimized — `useWatchScreen`'s `minimized`
 */
export function PlaybackHost({ children }: { children: ReactNode }) {
  const screen = useScreen();
  const routeTarget = useMemo(
    (): WatchTarget | null =>
      screen.kind === 'watch'
        ? {
            videoId: screen.videoId,
            startSec: screen.startSec,
            playlistId: screen.playlistId,
            index: screen.index,
          }
        : null,
    [screen],
  );

  const [session, setSession] = useState<WatchTarget | null>(routeTarget);

  /*
   * The setting is read here rather than anywhere the box is drawn, and that is the whole of
   * the switch: `session` is the only thing that outlives a watch route, so a session never taken is
   * the pre-feature behaviour exactly — the player is torn down on navigation and the stored resume
   * position makes that lossless. Turning it off with a corner player already up clears the session
   * on the same render, so the box goes rather than lingering until the next navigation.
   */
  const retain = useSettings().miniPlayer;

  /*
   * This tab has given its videos to another one and could not close (`tabs/`). It keeps no session
   * and draws no player: the queue is already cleared, and a corner player left standing would go on
   * playing a video that is now somebody else's tab's.
   */
  const handedOver = useHandedOver();

  /*
   * The queue's claim on the same player (2026-08-07). It is a second *origin* for a session rather
   * than a second player: a queue has to be able to start playing from a grid, with no watch route
   * ever having existed, and it has to keep playing across the navigations that are the whole point
   * of having a list. `queue/model.ts` builds the target; `offRouteTarget` decides between the two.
   */
  const queue = useQueue();
  const queueTarget = useMemo(() => queueWatchTarget(queue), [queue]);
  /** Whether the queue's own session may start itself — `queue/store.ts` for what decides it. */
  const queuePlays = useQueuePlayOnArrival();
  const queueHolds = useCallback(
    (videoId: string) => queue.entries.some((entry) => entry.videoId === videoId),
    [queue],
  );

  /*
   * Derived during render rather than in an effect, which is React's own recipe for state that
   * follows a prop — and here it is load-bearing rather than tidy. An effect would leave one
   * committed frame in which the new watch route is on screen and the session still names the
   * *previous* video, i.e. the old picture inside the new page. `session` is state and not a plain
   * derivation because it has to outlast the route that set it: that is the whole feature.
   *
   * **A queue keeps the session across the route even with the setting off**, and that is what
   * "honour the setting" means here rather than a hole in it. The switch decides whether playback
   * *follows the reader off the page*; a queue still needs a player to be a queue at all, so the box
   * stays and `PlaybackSession` pauses it instead (see `pauseOnLeave` below). With no queue, the
   * setting off is byte-for-byte what it always was.
   */
  const heldByQueue = !handedOver && routeTarget !== null && queueHolds(routeTarget.videoId);
  if (handedOver || (!retain && !heldByQueue)) {
    if (session !== null) setSession(null);
  } else if (routeTarget !== null && !sameWatchTarget(routeTarget, session)) setSession(routeTarget);

  /*
   * Which video the session is playing **from the queue**, kept from render to render because the
   * question the corner has to answer after an edit is a comparison rather than a state: a queue
   * that no longer holds the session's video has either just had that row removed from under the
   * player, or never held it at all, and only what was true a moment ago tells the two apart.
   *
   * State rather than a ref, since it is read during the render that also writes it — React's own
   * shape for a value derived from what the last render saw.
   */
  const sessionFromQueue =
    session !== null && queueHolds(session.videoId) ? session.videoId : null;
  const [playingFromQueue, setPlayingFromQueue] = useState(sessionFromQueue);
  if (playingFromQueue !== sessionFromQueue) setPlayingFromQueue(sessionFromQueue);

  const corner = offRouteTarget({ session, queue: queueTarget, queueHolds, playingFromQueue });
  // A session the queue has taken over, or one whose row it has dropped, is spent (`model.ts`). It
  // is cleared here rather than left standing because it would otherwise be what the corner falls
  // back to the moment the queue stops.
  if (routeTarget === null && corner.session !== session) setSession(corner.session);
  const current = routeTarget ?? corner.target;
  const release = useCallback(() => {
    setSession(null);
    /*
     * A queue's corner player *is* the queue, so closing the box ends the list (2026-08-08). It asks
     * nothing first: the ✕ and the swipe are how a reader puts this box away, and a queue that
     * survived being put away went on existing with nothing on screen to say so — the next press on
     * a card would then ask "play now or play next" about a list the reader believed they had
     * finished with.
     *
     * The same handler releases a corner player the queue has nothing to do with, and there is
     * nearly always nothing there to clear: arriving at a video the queue does not hold discards the
     * list before the corner ever shows it (`queue/useQueueAdvance.ts`), so that case is an empty
     * queue and clearing one is a no-op. The exception is a corner player stepped through a
     * *playlist* while a queue was running, where this ends both — and `hasQueue` is what makes the
     * ✕ say so before it is pressed.
     */
    clearQueue();
  }, []);

  /*
   * The two ways back into a list that has played out, from the box that says so (`QueueEnded.tsx`).
   * Both land on the last row — the one the list finished on (`queue/model.ts`) — and they differ only
   * in where the reader ends up: the **step back** plays it in the corner and leaves them on the page
   * they are reading, which is a cursor move like any other and therefore a request to play
   * (`queue/store.ts`); **Maximize** and the upward swipe open its watch page instead.
   *
   * Maximize moves no cursor itself and does not need to. Arriving at a watch route is what makes that
   * video the queue's current entry, so the route's own rule puts the cursor back on the row
   * (`queue/useQueueAdvance.ts`) — and it is a plain queue route, so the arrival keeps the list rather
   * than discarding it. Whether the video starts itself is then the Playback tab's "Start playing on
   * arrival", exactly as it is for a reader who opened the page from a link.
   *
   * The live snapshot rather than the render's, which is the rule every act on the queue keeps: no
   * handler may hold a `QueueState` long enough to write a stale one back.
   */
  const stepBackIntoQueue = useCallback(() => {
    const entry = queueLastEntry(readQueue());
    if (entry !== null) playQueueEntry(entry.key);
  }, []);
  const openLastQueueEntry = useCallback(() => {
    const live = readQueue();
    const entry = queueLastEntry(live);
    if (entry !== null) navigate(queueEntryHref(entry, live.link));
  }, []);

  /*
   * The queue outlives the video it was playing, so the corner does too (2026-08-08). `queueAdvance`
   * past the last row leaves the entries with no cursor, and so does a removal that takes the playing
   * **last** row out; either way `queueWatchTarget` answers `null` and there is nothing to put in a
   * player. The box stays and says the list is over (`QueueEnded.tsx`) rather than disappearing at
   * the one moment the reader is owed a sentence.
   *
   * `current === null` is the whole of "nothing else claims the corner": it is already false on
   * `/watch`, where the page has its own player, and false wherever a retained session is still in
   * the box. So the two conditions below are the state exactly.
   */
  // Before every arm below, and before `current` is consulted at all: on a watch route the route is
  // still naming a video, and the point of the handover is that this tab is not the one playing it.
  if (handedOver) return children;

  if (current === null) {
    if (queue.entries.length === 0) return children;
    // `children` stays the **first** child, which is what keeps closing this box from re-mounting the
    // whole routed page under it: React matches a single child against index 0 of the array it
    // replaces, so the page the reader is on keeps its state. The dock renders into a portal, so
    // where it sits in this tree has no bearing on where the box appears.
    return (
      <>
        {children}
        <QueueEndedDock
          onClose={release}
          onStepBack={stepBackIntoQueue}
          onMaximize={openLastQueueEntry}
        />
      </>
    );
  }
  return (
    // Keyed by video, exactly as `App.tsx` keyed the watch screen before this: a different video is a
    // different player. Navigating *back* to the retained route leaves the key alone, which is what
    // makes Maximize a reparent rather than a reload — and an advancing queue re-keys here, which is
    // how the corner plays through a list without navigating anywhere.
    <PlaybackSession
      key={current.videoId}
      target={current}
      minimized={routeTarget === null}
      queued={queueHolds(current.videoId)}
      hasQueue={queue.entries.length > 0}
      queuePlays={queuePlays}
      pauseOnLeave={!retain}
      onRelease={release}
    >
      {children}
    </PlaybackSession>
  );
}

function PlaybackSession({
  target,
  minimized,
  queued,
  hasQueue,
  queuePlays,
  pauseOnLeave,
  onRelease,
  children,
}: {
  readonly target: WatchTarget;
  readonly minimized: boolean;
  /**
   * The queue holds this video, so the corner player is the queue playing rather than a video the
   * reader walked away from. It is what keeps a *paused* corner player alive: releasing one because
   * nobody pressed play would throw a list away over a pause.
   */
  readonly queued: boolean;
  /**
   * There is a list to lose, whether or not this video is in it. Only the ✕'s **name** hangs off it:
   * closing the box clears the queue either way, and a control that ends a list somebody built says
   * so rather than promising to close a player.
   */
  readonly hasQueue: boolean;
  /**
   * Whether *this* queue session starts itself. The queue store decides (`queue/store.ts`): moving
   * the cursor is a request to play, creating a queue is an arrival and the Playback tab's setting
   * has the say there.
   */
  readonly queuePlays: boolean;
  /**
   * "Keep playing when you leave the video" is off. Without a queue this component never runs off
   * the route at all; with one it does, and this is what the setting then means — the player stays
   * because the queue needs it, and the playback stops because the reader asked for that.
   */
  readonly pauseOnLeave: boolean;
  readonly onRelease: () => void;
  readonly children: ReactNode;
}) {
  /*
   * Whether this session may start itself, in the two arms a corner player has. `undefined` leaves the
   * Playback tab's "Start playing on arrival" in charge, which is every session that came from a watch
   * route — the reader opened a page, and that setting is about pages.
   *
   * The queue's arm ends in `!pauseOnLeave`, because "Keep playing when you leave the video" keeps its
   * veto over it: with the setting off, playback does not follow the reader off the page, so a queue
   * built while they are elsewhere arrives ready rather than running. Every way the corner moves through
   * a list — an advance, its own back and forward buttons, a press on a panel row — is a cursor move, so
   * the queue store answers all of them at once and there is no second arm for a press by hand.
   */
  const autoplay = !minimized
    ? undefined
    : queued
      ? // What the queue asked for: an advance or a press on a row plays, a queue *created* from a
        // grid honours the arrival setting (`queue/store.ts`).
        queuePlays && !pauseOnLeave
      : undefined;

  const binding = useWatchScreen(target, { minimized, autoplay });

  /*
   * What the corner says about a video it cannot play, and whether it is drawn at all
   * (`screens/watch/model.ts`). Both come off the stage, because a session that never reached a
   * player has no surface to hang a message on: without this the frame stayed empty and collapsed to
   * nothing, which is how a queue whose entry is private, region-locked or simply gone read as a
   * corner player that had quietly died. A queue is what makes the state reachable — a video nobody
   * pressed play on is otherwise released the moment it is minimized (below), while a queued one
   * stays and has to say something.
   *
   * Minimized only: on the watch page the same refusal is already the page's own full-size box, with
   * the detail, Try again and the Watch on menu this line points at.
   */
  const notice = minimized && binding.external === null ? miniStageNotice(binding.stage) : null;

  /*
   * Leaving the page stops the playback when the setting is off. A layout effect on the transition
   * rather than a condition on the render, because it is an act on the player and not a state: a
   * reader who presses play on the corner player afterwards must keep it, and a rule that re-asserted
   * itself on every render would take it straight back off them.
   */
  useLayoutEffect(() => {
    if (minimized && pauseOnLeave) binding.handlers.command({ kind: 'pause' });
    // `binding.handlers` is deliberately not a dependency: this fires on the transition into the
    // corner, not whenever the command dispatcher happens to be rebuilt — and it is rebuilt on the
    // 4 Hz transport poll, which would re-pause a player the reader had just restarted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimized, pauseOnLeave]);

  /*
   * The factory builds detached elements and touches nothing — see `dock.ts` for why that is a rule
   * and not a preference. StrictMode calls this twice; the loser is garbage, not a second frame in
   * the document.
   */
  const dock = useMemo(() => createPlayerDock(document), []);

  useLayoutEffect(() => {
    dock.attach(document.body);
    return () => dock.detach();
  }, [dock]);

  useLayoutEffect(() => {
    dock.setMinimized(minimized);
  }, [dock, minimized]);

  /*
   * Fullscreen is a property of the element being minimized, and moving it out of the fullscreen
   * stack while the browser still believes it is there leaves a black screen with no chrome and no
   * way back. Leaving fullscreen first is the only ordering that ends somewhere the reader can see.
   */
  useLayoutEffect(() => {
    if (minimized && document.fullscreenElement !== null) {
      void document.exitFullscreen().catch(() => undefined);
    }
  }, [minimized]);

  /*
   * "While a video is playing", read literally. The sample is at most one poll tick old,
   * which is the right resolution for a question about the instant the reader clicked a link — and
   * `playing` stays true through a rebuffer, so a stall mid-navigation does not throw the session
   * away. A paused player is released instead: it tears down exactly as it did before this existed,
   * and the resume position it wrote is what makes that lossless.
   */
  const playing = binding.transport.playing;
  const playingRef = useRef(playing);
  useLayoutEffect(() => {
    playingRef.current = playing;
  }, [playing]);
  useLayoutEffect(() => {
    // Reads the ref rather than `playing` so that a *later* pause cannot retroactively close a
    // minimized player the reader is looking at: the question is only ever asked at the transition.
    //
    // A queued video is exempt, and it has to be. This rule exists because a paused player nobody
    // carried out of the page on purpose is better torn down than left in a corner — but a queue's
    // corner player is the queue, so releasing it would silently stop the list, and with the setting
    // off it is paused *by us* one effect earlier, which would make the box impossible to keep.
    //
    // The exemption also covers the sharper case a step through the list is (2026-08-07): that session
    // does not become minimized, it *starts* minimized, on a fresh mount whose transport has not
    // reported a frame yet — so unguarded this would close the corner player in the same tick the
    // forward button opened it. Every step is a cursor move now, so the video it lands on is one the
    // queue holds and this one test answers both.
    if (minimized && !queued && !playingRef.current) onRelease();
  }, [minimized, queued, onRelease]);

  const slotRef = useMemo(() => dock.setSlot, [dock]);
  const mini = useMemo(
    () => ({
      onMaximize: () => navigate(maximizeHref(target)),
      onClose: onRelease,
      closesQueue: hasQueue,
      // The one thing on the minimized player that the surface cannot do to itself: the frame is the
      // dock's element, so a swipe that moves the whole box has to be handed back here.
      follow: dock.follow,
    }),
    [target, onRelease, hasQueue, dock],
  );
  const playback = useMemo(() => ({ binding, slotRef }), [binding, slotRef]);

  return (
    <>
      {/*
       * Portalled into a node this app owns rather than rendered in place, and that is what makes
       * the whole arrangement safe: React only ever mutates the *contents* of `host`, never where it
       * sits, so `dock.ts` can move it between the page and the corner without React and the DOM
       * disagreeing about the tree.
       */}
      {/*
       * An iframe leg (§8's Switch player) is drawn inside the surface, so it needs the surface
       * whatever the stage says: it is also offered when Visious's own player failed.
       */}
      {createPortal(
        binding.stage.kind === 'player' || binding.external !== null || notice !== null ? (
          <PlayerSurface binding={binding} mini={minimized ? mini : null} notice={notice} />
        ) : null,
        dock.host,
      )}
      {/*
       * `children` is one unchanging element, so the routed tree does not re-render with this
       * component's four-times-a-second transport poll — only the components that actually read the
       * context do, which is the watch page and nothing else.
       */}
      <PlaybackProvider value={playback}>{children}</PlaybackProvider>
    </>
  );
}
